import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import { z } from 'zod'

import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { VStack } from '~/components/elements/VStack'
import { SurveyFilterGroupsInput } from '~/components/features/SurveyFilterGroupsInput'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery'
import { type CustomerAttribute } from '~/hooks/useSurveyQuery'
import { exists, surveyFilterGroupsSchema } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useAvailableBrandGroups } from '../hooks/useAvailableBrandGroups'
import { useAvailableCustomerAttributeGroups } from '../hooks/useAvailableCustomerAttributeGroups'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateCustomerAttributeMutation } from '../hooks/useCreateCustomerAttributeMutation'
import { useUpdateCustomerAttributeMutation } from '../hooks/useUpdateCustomerAttributeMutation'

import styles from './EditCustomerAttributeDialog.css'

const schema = z.object({
  name: z.string().min(1),
  filterGroups: surveyFilterGroupsSchema.min(1),
})
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditCustomerAttributeDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const matchDialogInfo = React.useCallback(
    <T,>(
      withCreateOneByOne: () => T,
      withUpdate: (customerAttribute: CustomerAttribute) => T,
    ) =>
      match(dialogInfo)
        .with({ dialogType: 'create' }, () => withCreateOneByOne())
        .with({ dialogType: 'update' }, ({ customerAttribute }) =>
          withUpdate(customerAttribute),
        )
        .run(),
    [dialogInfo],
  )

  const methods = useForm<InputValues>({
    defaultValues: matchDialogInfo(
      () => ({ name: '', filterGroups: [[]] }),
      (customerAttribute) => customerAttribute as InputValues,
    ),
    resolver,
  })
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = methods
  const nameField = useFormField(control, 'name')

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const createCustomerAttributeMutation = useCreateCustomerAttributeMutation({
    onSuccess: (data) => {
      // 対象グループが見つからない場合や絞り込み設定が無効な場合、createCustomerAttributeはcustomerAttributeを返さない。作成できていないので記録しない。
      if (exists(data.createCustomerAttribute?.customerAttribute)) {
        logEvent('Create customer attribute')
      }
      closeDialogWithRefresh()
    },
  })
  const updateCustomerAttributeMutation = useUpdateCustomerAttributeMutation({
    onSuccess: (data) => {
      // 対象が見つからない場合や絞り込み設定が無効な場合、updateCustomerAttributeはcustomerAttributeを返さない。更新できていないので記録しない。
      if (exists(data.updateCustomerAttribute?.customerAttribute)) {
        logEvent('Edit customer attribute')
      }
      closeDialogWithRefresh()
    },
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    (input) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      matchDialogInfo(
        () => {
          createCustomerAttributeMutation.mutate(input)
        },
        (customerAttribute) => {
          updateCustomerAttributeMutation.mutate({
            customerAttributeId: customerAttribute.id,
            input,
          })
        },
      )
    },
    [
      createCustomerAttributeMutation,
      isDirty,
      matchDialogInfo,
      resetDialogInfo,
      updateCustomerAttributeMutation,
    ],
  )

  const brandGroups = useAvailableBrandGroups()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups(
    matchDialogInfo(
      () => undefined,
      (customerAttribute) => customerAttribute,
    ),
  )
  const questions = useSurveyQueryQuestions()

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        disabledExecute={!isValid}
        executing={
          createCustomerAttributeMutation.isPending ||
          updateCustomerAttributeMutation.isPending
        }
        mode={matchDialogInfo<ExecuteCancelDialogProps['mode']>(
          () => 'create',
          () => 'settings',
        )}
        title={t('set_customized_segments')}
        onCancel={resetDialogInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack className={styles.form} spacing={8}>
          <LabelingContainer label={t('customized_segments_name')}>
            <StyledInput width="100%" {...nameField} />
          </LabelingContainer>

          <SurveyFilterGroupsInput
            brandGroups={brandGroups}
            // @ts-expect-error
            control={control}
            customerAttributeGroups={customerAttributeGroups}
            questions={questions}
          />
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
